import { useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Layout } from '../components/Layout';
import { useUpcomingEvents } from '../hooks/useEvents';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { EventCard } from '../components/EventCard';
import { EventsLoadingStates } from '../components/EventsLoadingStates';
import { useEventFlow } from '../hooks/useEventFlow';
import { Input } from '../components/ui/input';
import { Search, X } from 'lucide-react';
import { Event } from '../types/event.types';
import { EventStatus } from '../types/enums';

import { useMyFamilies } from '../hooks/useMyFamilies';
import { UserStatus } from '../types/enums';
import { useRefreshContext } from '@/contexts/RefreshContext';
import { useQueryClient } from '@tanstack/react-query';

// Static array of allowed event statuses for member view
const ALLOWED_EVENT_STATUSES: EventStatus[] = [
  EventStatus.DRAFT,
  EventStatus.OPENED,
  EventStatus.REG_OPENED,
  EventStatus.REG_CLOSED,
];

export const EventsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const [searchQuery, setSearchQuery] = useState('');
  const { registerRefresh, unregisterRefresh } = useRefreshContext();
  const queryClient = useQueryClient();
  const { data: events = [], isLoading, error } = useUpcomingEvents();
  const { families } = useMyFamilies();

  // Check if we're in "today only" mode (from Check-in Today button)
  const isTodayOnly = location.state?.todayOnly === true;

  const handleShowAllEvents = () => {
    navigate('/events', { replace: true });
  };

  // Register refresh function that invalidates cache and refetches
  useEffect(() => {
    registerRefresh('/events', async () => {
      await queryClient.invalidateQueries({ queryKey: ['member-events'] });
    });
    return () => unregisterRefresh('/events');
  }, [registerRefresh, unregisterRefresh, queryClient]);

  const hasActiveFamily = useMemo(() => {
    // If no families, user needs to create one, so we show buttons (which likely lead to auth/creation flows)
    // Or if they have at least one active family
    return families.length === 0 || families.some((f) => f.status === UserStatus.ACTIVE);
  }, [families]);

  const { selectedMembers, memberDetails, isSelectingForFamily, handleEventSelect, handleCheckIn, isEventCheckedIn } =
    useEventFlow();

  // Show events only with allowed statuses
  const statusFilteredEvents = useMemo(() => {
    return events.filter((event) => {
      return event.status && ALLOWED_EVENT_STATUSES.includes(event.status);
    });
  }, [events]);

  const filteredEvents = useMemo(() => {
    if (!searchQuery.trim()) return statusFilteredEvents;

    const query = searchQuery.toLowerCase();
    return statusFilteredEvents.filter(
      (event) =>
        event.name?.toLowerCase().includes(query) ||
        event.location?.toLowerCase().includes(query) ||
        event.description?.toLowerCase().includes(query),
    );
  }, [statusFilteredEvents, searchQuery]);

  const groupedEvents = useMemo(() => {
    if (!filteredEvents?.length) {
      return { todayEvents: [], futureEvents: [] };
    }

    const now = new Date();
    const todayUtcStart = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());

    const todayUtcEnd = todayUtcStart + 24 * 60 * 60 * 1000;

    const todayEvents: typeof filteredEvents = [];
    const futureEvents: typeof filteredEvents = [];

    filteredEvents.forEach((event) => {
      if (!event.startDateTime) return;

      // Use new Date() for more robust parsing and explicitly check for validity
      const eventDate = new Date(event.startDateTime);
      const eventTs = eventDate.getTime();

      if (isNaN(eventTs)) {
        console.warn('Invalid date for event:', event.name, event.startDateTime);
        return;
      }

      if (eventTs >= todayUtcStart && eventTs < todayUtcEnd) {
        todayEvents.push(event);
      } else if (eventTs >= todayUtcEnd) {
        futureEvents.push(event);
      }
    });

    const sortByTs = (a: Event, b: Event) => new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime();

    todayEvents.sort(sortByTs);
    futureEvents.sort(sortByTs);

    return { todayEvents, futureEvents };
  }, [filteredEvents]);

  return (
    <Layout>
      <div className="space-y-4 md:space-y-6 max-w-none">
        <Card className="card-gradient border-[var(--color-secondary-300)]">
          <CardContent className="pt-4 md:pt-6">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
              <div className="flex-1">
                <h2 className="text-lg md:text-xl font-medium text-[var(--color-secondary-700)]">
                  {isSelectingForFamily ? t('events.selectedMembers') : t('events.browseEvents')}
                </h2>
                <p className="text-[var(--color-secondary-700)] text-sm md:text-base mt-1">
                  {isSelectingForFamily
                    ? t('events.membersSelected', { count: selectedMembers.length })
                    : t('events.selectionPrompt')}
                </p>
              </div>
              {isSelectingForFamily && (
                <Button
                  variant="outline"
                  size="md"
                  onClick={() => navigate('/family')}
                  className="text-[var(--color-secondary-500)] hover:text-[var(--color-secondary-700)] w-full md:w-auto"
                >
                  {t('buttons.changeSelection')}
                </Button>
              )}
            </div>

            <div className="relative mt-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--color-neutral-400)] h-4 w-4" />
              <Input
                type="text"
                placeholder={t('search.placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-10"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {searchQuery && (
          <div className="text-sm text-[var(--color-neutral-700)]">
            {filteredEvents.length === 0 ? (
              <p>{t('search.noResults', { query: searchQuery })}</p>
            ) : (
              <p>{t('search.resultsCount', { count: filteredEvents.length, query: searchQuery })}</p>
            )}
          </div>
        )}

        {/* Today Only Mode Banner */}
        {isTodayOnly && (
          <Card className="bg-[var(--color-primary-50)] border-[var(--color-primary-200)]">
            <CardContent className="py-3 px-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <span className="text-sm text-[var(--color-primary-700)] leading-snug">
                  {t('events.showingTodayOnly')}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleShowAllEvents}
                  className="text-[var(--color-primary-600)] border-[var(--color-primary-300)] hover:bg-[var(--color-primary-100)] whitespace-nowrap flex-shrink-0 w-full sm:w-auto"
                >
                  {t('events.showAllEvents')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <EventsLoadingStates isLoading={isLoading} error={error} eventsCount={filteredEvents.length}>
          <div className="space-y-6">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-[var(--color-primary-700)]">{t('labels.today')}</h3>
                {groupedEvents.todayEvents.length > 0 && (
                  <span className="text-sm text-[var(--color-neutral-500)]">({groupedEvents.todayEvents.length})</span>
                )}
              </div>
              {groupedEvents.todayEvents.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 justify-items-center">
                  {groupedEvents.todayEvents.map((event) => (
                    <EventCard
                      key={event.id}
                      event={event}
                      selectedMembers={selectedMembers}
                      memberDetails={memberDetails}
                      isSelectingForFamily={isSelectingForFamily}
                      isCheckedIn={isEventCheckedIn(event.id)}
                      onEventSelect={(eventId) => handleEventSelect(eventId, statusFilteredEvents)}
                      onCheckIn={(eventId) => handleCheckIn(eventId, statusFilteredEvents)}
                      showActionButton={hasActiveFamily}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 px-4 text-[var(--color-neutral-500)]">
                  <p className="font-medium">{t('events.noEventsScheduled')}</p>
                  {isTodayOnly && (
                    <>
                      <p className="text-sm mt-2 leading-relaxed max-w-md mx-auto">
                        {t('events.noEventsScheduledDescription')}
                      </p>
                      <Button variant="outline" size="sm" onClick={handleShowAllEvents} className="mt-4">
                        {t('events.showAllEvents')}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Coming Soon section - hidden in todayOnly mode */}
            {!isTodayOnly && groupedEvents.futureEvents.length > 0 && (
              <hr className="border-[var(--color-neutral-200)]" />
            )}

            {!isTodayOnly && (groupedEvents.todayEvents.length === 0 || groupedEvents.futureEvents.length > 0) && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-[var(--color-neutral-700)]">{t('labels.comingSoon')}</h3>
                  {groupedEvents.futureEvents.length > 0 && (
                    <span className="text-sm text-[var(--color-neutral-500)]">
                      ({groupedEvents.futureEvents.length})
                    </span>
                  )}
                </div>
                {groupedEvents.futureEvents.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 justify-items-center">
                    {groupedEvents.futureEvents.map((event) => (
                      <EventCard
                        key={event.id}
                        event={event}
                        selectedMembers={selectedMembers}
                        memberDetails={memberDetails}
                        isSelectingForFamily={isSelectingForFamily}
                        isCheckedIn={isEventCheckedIn(event.id)}
                        onEventSelect={(eventId) => handleEventSelect(eventId, events)}
                        onCheckIn={(eventId) => handleCheckIn(eventId, events)}
                        showActionButton={hasActiveFamily}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-[var(--color-neutral-500)]">
                    <p>{t('events.noUpcomingEvents')}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </EventsLoadingStates>
      </div>
    </Layout>
  );
};
